import { Plus } from "lucide-react";
import { Button } from "@babackapp/ui/components/button";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import {
  createEmptyCondition,
  FieldIdDefinition,
  type ExcludeFieldOptions,
} from "@/lib/override-rules";
import { ConditionRow } from "./condition-row";
import type {
  ConditionalRulesDto,
  ConditionalRuleDto,
} from "@babackapp/private-api-client/types";
import {
  FormField,
  FormItem,
  FormMessage,
} from "@babackapp/ui/components/form";
import useTranslation from "@babackapp/translations/use-translation";

interface RuleConditionsBuilderProps {
  value: ConditionalRulesDto;
  onChange: (value: ConditionalRulesDto) => void;
  disabled?: boolean;
  excludeFields?: FieldIdDefinition[];
  excludeFieldOptions?: ExcludeFieldOptions;
}

export function RuleConditionsBuilder({
  value,
  onChange,
  disabled,
  excludeFields,
  excludeFieldOptions,
}: RuleConditionsBuilderProps) {
  const t = useTranslation();
  const handleLogicalOperatorChange = (operator: "AND" | "OR") => {
    onChange({ ...value, operator });
  };

  const handleConditionChange = (
    index: number,
    condition: ConditionalRuleDto,
  ) => {
    const newRules = [...value.rules];
    newRules[index] = condition;
    onChange({ ...value, rules: newRules });
  };

  const handleAddCondition = () => {
    const newCondition = createEmptyCondition();
    onChange({
      ...value,
      rules: [...value.rules, newCondition],
    });
  };

  const handleRemoveCondition = (index: number) => {
    if (value.rules.length > 1) {
      const newRules = value.rules.filter((_, i) => i !== index);
      onChange({ ...value, rules: newRules });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {t("overrideRules.misc.conditions")}
        </span>
      </div>

      <div className="space-y-1">
        {value.rules.map((condition, index) => (
          <div
            key={`${condition.id}-${index}`}
            className="flex flex-col items-stretch gap-2"
          >
            {index === 1 && (
              <Select
                value={value.operator}
                onValueChange={handleLogicalOperatorChange}
                disabled={disabled}
              >
                <SelectTrigger className="w-16 h-6 text-xs font-semibold min-w-fit self-center">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">
                    {t("misc.and").toUpperCase()}
                  </SelectItem>
                  <SelectItem value="OR">
                    {t("misc.or").toUpperCase()}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            {index > 1 && (
              <Badge
                variant="outline"
                className="text-xs px-2 py-0 h-5 self-center"
              >
                {t(
                  `misc.${value.operator.toLowerCase() as "and" | "or"}`,
                ).toUpperCase()}
              </Badge>
            )}

            <FormField
              name={`conditions.rules.${index}`}
              render={() => (
                <FormItem className="overflow-hidden">
                  <ConditionRow
                    conditionIndex={index}
                    condition={condition}
                    onChange={(updatedCondition) =>
                      handleConditionChange(index, updatedCondition)
                    }
                    onRemove={() => handleRemoveCondition(index)}
                    canRemove={value.rules.length > 1}
                    disabled={disabled}
                    excludeFields={excludeFields}
                    excludeFieldOptions={excludeFieldOptions}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}

        {/* Add button at the end of rules */}
        <div className="flex justify-center mt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddCondition}
            disabled={disabled}
            className="h-7 px-2 text-xs"
          >
            <Plus className="h-3 w-3" />
            {t("misc.add")}
          </Button>
        </div>
      </div>

      {value.rules.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          <Button
            type="button"
            variant="ghost"
            onClick={handleAddCondition}
            disabled={disabled}
            size="sm"
          >
            <Plus className="h-4 w-4" />
            {t("overrideRules.misc.addCondition")}
          </Button>
        </div>
      )}
    </div>
  );
}
